(() => {
  const loader =
    document.currentScript || document.querySelector("script[data-cart-script]");
  const cartScripts = [
    loader?.dataset.cartScript,
    loader?.dataset.cartDrawerScript,
    loader?.dataset.cartNoteScript,
  ].filter(Boolean);
  const cartCrossSellStyles = [
    loader?.dataset.cartCrossSellVideoStyle,
    loader?.dataset.cartCrossSellImageStyle,
  ].filter(Boolean);
  const cartCrossSellSliderScript =
    loader?.dataset.cartCrossSellSliderScript;
  let cartReadyPromise;
  let pendingCartTrigger = null;
  let pendingCartForm = null;

  const isCartReady = () => {
    const cart = document.querySelector("cart-drawer");
    return (
      cart &&
      typeof cart.getSectionsToRender === "function" &&
      typeof cart.renderContents === "function"
    );
  };

  const isScriptAlreadyLoaded = (script) => {
    if (script.dataset.cartLoaderReady === "true") return true;
    if (
      script.readyState === "complete" ||
      script.readyState === "loaded"
    ) {
      return true;
    }
    return performance
      .getEntriesByName(script.src, "resource")
      .some((entry) => entry.responseEnd > 0);
  };

  const loadScript = (src, type = "text/javascript") =>
    new Promise((resolve, reject) => {
      const existingScript = Array.from(document.scripts).find(
        (script) => script.src === src,
      );

      if (existingScript) {
        if (isScriptAlreadyLoaded(existingScript)) {
          existingScript.dataset.cartLoaderReady = "true";
          resolve();
          return;
        }

        existingScript.addEventListener(
          "load",
          () => {
            existingScript.dataset.cartLoaderReady = "true";
            resolve();
          },
          { once: true },
        );
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      if (type === "module") script.type = "module";
      script.async = false;
      script.addEventListener(
        "load",
        () => {
          script.dataset.cartLoaderReady = "true";
          resolve();
        },
        { once: true },
      );
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

  const loadStyle = (href) =>
    new Promise((resolve, reject) => {
      const existingStyle = Array.from(
        document.querySelectorAll('link[rel="stylesheet"]'),
      ).find((link) => link.href === href);

      if (existingStyle) {
        resolve();
        return;
      }

      const style = document.createElement("link");
      style.rel = "stylesheet";
      style.href = href;
      style.addEventListener("load", resolve, { once: true });
      style.addEventListener("error", reject, { once: true });
      document.head.appendChild(style);
    });

  const ensureCartReady = () => {
    if (isCartReady()) return Promise.resolve();
    if (cartReadyPromise) return cartReadyPromise;

    cartReadyPromise = cartScripts
      .reduce((promise, src) => promise.then(() => loadScript(src)), Promise.resolve())
      .then(() => customElements.whenDefined("cart-drawer"))
      .catch((error) => {
        cartReadyPromise = null;
        throw error;
      });

    return cartReadyPromise;
  };

  const loadCartCrossSell = () => {
    const crossSell = document.querySelector(".wt-cart__cross-sell");
    if (!crossSell) return Promise.resolve();

    return Promise.all(cartCrossSellStyles.map(loadStyle)).then(() => {
      if (
        crossSell.querySelector("slideshow-section") &&
        !customElements.get("slideshow-section") &&
        cartCrossSellSliderScript
      ) {
        return loadScript(cartCrossSellSliderScript, "module");
      }
    });
  };

  const isAddToCartForm = (form) =>
    form instanceof HTMLFormElement &&
    (form.matches('form[action*="/cart/add"]') ||
      form.closest("product-form") !== null);

  document.addEventListener(
    "submit",
    (event) => {
      if (isCartReady() || !isAddToCartForm(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const form = event.target;
      if (pendingCartForm) return;
      pendingCartForm = form;

      ensureCartReady()
        .then(() => {
          pendingCartForm = null;
          if (form.isConnected) form.requestSubmit();
        })
        .catch(() => {
          pendingCartForm = null;
          if (form.isConnected) form.submit();
        });
    },
    true,
  );

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (
        event.target.closest('[name="add"], [type="submit"], .wt-cart__trigger')
      ) {
        ensureCartReady();
      }
    },
    { capture: true, passive: true },
  );

  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest(".wt-cart__trigger");
      if (!trigger || isCartReady()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (pendingCartTrigger) return;
      pendingCartTrigger = trigger;

      ensureCartReady()
        .then(() => {
          pendingCartTrigger = null;
          if (trigger.isConnected) trigger.click();
        })
        .catch(() => {
          pendingCartTrigger = null;
          if (trigger.href) window.location.assign(trigger.href);
        });
    },
    true,
  );

  document.addEventListener("cart-drawer-open", () => {
    loadCartCrossSell().catch(() => undefined);
  });

  const warmCart = () => ensureCartReady().catch(() => undefined);
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warmCart, { timeout: 3000 });
  } else {
    window.setTimeout(warmCart, 1500);
  }
})();
